import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Disc3, Search } from "lucide-react";
import api from "@/lib/api";
import { setSeo } from "@/lib/events";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { NumberOnesRecords } from "@/components/NumberOnesRecords";
import { NumberOnesThisWeek } from "@/components/NumberOnesThisWeek";
import { ListenButton } from "@/components/ListenButton";

const RunRow = ({ r }) => (
  <li data-testid={`no1-run-${r.start}`} className="flex items-start gap-4 py-3 border-b border-[#f1f1f1] last:border-0">
    <span className="w-28 sm:w-40 shrink-0 text-[12px] text-ink-muted leading-snug pt-0.5">
      {r.start_label}{r.end !== r.start && <><br className="sm:hidden" /><span className="hidden sm:inline"> – </span>{r.end_label}</>}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-[15px] font-semibold text-ink">{r.title}</span>
      <span className="block text-[13px] text-ink-muted">{r.artist}</span>
    </span>
    <span className="shrink-0 text-right">
      <span className="block text-[18px] font-bold text-gold leading-none">{r.weeks}</span>
      <span className="block text-[10px] uppercase tracking-wider text-ink-muted mt-0.5">wk{r.weeks === 1 ? "" : "s"}</span>
    </span>
    <ListenButton artist={r.artist} title={r.title} testid={`no1-listen-${r.start}`} />
  </li>
);

export default function NumberOnes() {
  const [params, setParams] = useSearchParams();
  const decade = Number(params.get("decade")) || null;
  const q = params.get("q") || "";
  const tab = ["records", "years"].includes(params.get("tab")) ? params.get("tab") : params.get("q") ? "years" : "week";
  const chart = params.get("chart") || "uk_singles";
  const [charts, setCharts] = useState([]);
  useEffect(() => { api.get("/number-ones/charts").then((r) => setCharts(r.data.charts)).catch(() => {}); }, []);
  useEffect(() => { setSeo({ title: "Every UK & US No.1 Single and Album — what was number 1 on this day? | OddlyTrue", description: "Every UK Singles, UK Albums, Billboard Hot 100 and Billboard 200 number one since 1952: what was No.1 on this day in history, the week you were born, record-breaking runs and a daily chart quiz.", canonical: "https://oddlytrue.co/number-ones" }); }, []);
  const meta = charts.find((c) => c.chart === chart) || { label: "UK Singles", kind: "single", region: "UK", source: "", first: "1952-11-14" };
  const withChart = (o) => ({ chart, ...o });
  const [input, setInput] = useState(q);
  const [data, setData] = useState({ runs: [], decades: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (input === q) return undefined;
    const t = setTimeout(() => {
      const next = {};
      if (tab !== "week") next.tab = tab;
      if (decade) next.decade = decade;
      if (input.trim()) next.q = input.trim();
      setParams(withChart(next), { replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    const first = Number(meta.first.slice(0, 3)) * 10;
    api.get("/number-ones/runs", { params: { chart, decade: q ? undefined : decade || first, q: q || undefined } })
      .then((r) => setData(r.data)).catch(() => setData({ runs: [], decades: [], total: 0 })).finally(() => setLoading(false));
  }, [decade, q, chart, meta.first]);

  const byYear = useMemo(() => {
    const m = new Map();
    data.runs.forEach((r) => { if (!m.has(r.year)) m.set(r.year, []); m.get(r.year).push(r); });
    return [...m.entries()];
  }, [data.runs]);

  const activeDecade = q ? null : decade || Number(meta.first.slice(0, 3)) * 10;

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="number-ones-page">
        <div className="flex items-center gap-2">
          <Disc3 className="w-4 h-4 text-gold" />
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">No.1 Charts · {meta.source || "Official charts"}</p>
        </div>
        <h1 data-testid="no1-h1" className="mt-2 text-4xl sm:text-5xl font-bold text-ink leading-tight">{tab === "week" ? `What was No.1 this week in history` : tab === "records" ? `${meta.region} ${meta.kind} chart records` : `Every ${meta.region} No.1 ${meta.kind} since ${meta.first.slice(0, 4)}`}</h1>
        <p className="mt-3 text-base text-ink-muted max-w-2xl">{tab === "week" ? `The ${meta.region} No.1 ${meta.kind} in this week of the year, every year back to ${meta.first.slice(0, 4)} — so you know what was top of the charts on this day in history.` : `One row per ${meta.kind}, with its run at the top — UK and US, singles and albums, from the first official chart to this week.`}</p>

        <div className="mt-6 flex flex-wrap gap-2" data-testid="no1-chart-tabs">
          {(charts.length ? charts : [{ chart: "uk_singles", label: "UK Singles" }]).map((c) => (
            <button key={c.chart} type="button" data-testid={`no1-chart-${c.chart}`} onClick={() => { setInput(""); setParams(tab === "week" ? { chart: c.chart } : { chart: c.chart, tab }, { replace: true }); }}
              className={`px-4 h-10 rounded-full text-[13px] font-semibold border transition-colors ${chart === c.chart ? "bg-gold border-gold text-ink" : "bg-white border-[#e5e7eb] text-ink-muted hover:border-gold hover:text-ink"}`}>
              {c.label}
            </button>
          ))}
        </div>

        <div className="mt-6 flex gap-1 bg-white border border-[#e5e7eb] rounded-full p-1 w-fit" role="tablist" data-testid="no1-tabs">
          {[["week", "This week in history"], ["years", "Every No.1"], ["records", "Records"]].map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} data-testid={`no1-tab-${id}`}
              onClick={() => setParams(withChart(id === "week" ? {} : id === "records" ? { tab: "records" } : { tab: "years", ...(decade ? { decade } : {}) }), { replace: true })}
              className={`px-5 h-9 rounded-full text-[13px] font-semibold transition-colors ${tab === id ? "bg-ink text-white" : "text-ink-muted hover:text-ink"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "week" ? <NumberOnesThisWeek chart={chart} meta={meta} /> : tab === "records" ? <NumberOnesRecords chart={chart} /> : (<>
        <div className="mt-6 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-muted" />
          <input
            data-testid="no1-search"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={`Search ${meta.label} — artist or title…`}
            className="w-full h-12 rounded-full border border-[#e5e7eb] bg-white pl-11 pr-4 text-[15px] text-ink placeholder:text-ink-muted focus:border-gold outline-none"
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="no1-decades">
          {(data.decades.length ? data.decades : [1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020]).map((d) => (
            <button key={d} type="button" data-testid={`no1-decade-${d}`} onClick={() => { setInput(""); setParams({ chart, tab: "years", decade: d }, { replace: true }); }}
              className={`px-4 h-9 rounded-full text-[13px] font-semibold transition-colors ${activeDecade === d ? "bg-ink text-white" : "bg-white border border-[#e5e7eb] text-ink-muted hover:border-gold hover:text-ink"}`}>
              {d}s
            </button>
          ))}
        </div>

        <AdSlot id="adsense-number-ones" slot="TOP" size="leaderboard" className="my-8" />

        {loading ? (
          <p className="mt-8 text-sm text-ink-muted">Loading the charts…</p>
        ) : data.runs.length === 0 ? (
          <p data-testid="no1-empty" className="mt-8 text-sm text-ink-muted">No No.1s match "{q}". Try another artist or title.</p>
        ) : (
          <>
            <p className="mt-2 text-[12px] text-ink-muted" data-testid="no1-count">{data.total} chart-topping run{data.total === 1 ? "" : "s"}{q ? ` matching "${q}"` : ` in the ${activeDecade}s`}</p>
            <div className="mt-4 space-y-8">
              {byYear.map(([year, runs]) => (
                <section key={year} data-testid={`no1-year-${year}`}>
                  <h2 className="text-lg font-bold text-ink border-b-2 border-ink pb-2 flex items-baseline justify-between">
                    <span>{year}</span><span className="text-[12px] font-semibold text-ink-muted">{runs.length} No.1{runs.length === 1 ? "" : "s"}</span>
                  </h2>
                  <ul className="bg-white border border-[#e5e7eb] rounded-2xl px-4 mt-3">
                    {runs.map((r) => <RunRow key={r.start} r={r} />)}
                  </ul>
                </section>
              ))}
            </div>
          </>
        )}
        </>)}
        <p className="mt-10 text-[11px] text-ink-muted">Source: {meta.source}. Weekly chart dates; a run is counted in consecutive chart weeks.</p>
      </main>
      <Footer />
    </div>
  );
}
